import { useNavigate } from "react-router-dom";
import {
    deleteActiveOrgCompany,
    deleteActiveOrgCustomer,
} from "Entity/ActiveOrgHandler";
import {
    ActiveAuthUserContext,
    ActiveNorreUserContext,
    deleteActiveNorreUserStorage,
    deleteActiveUserStorage,
} from "Entity/ActiveUser";
import {
    NorreFunctionalityContext,
    removeNorreFunctionalityStorage,
} from "Entity/Functionality";
import { useApolloClient } from "@apollo/client";
import { LOGIN_ABSOLUTE } from "urls";
import client from "Apollo/config";
import { clearTokenExpiration, hasValidToken } from "Entity/Authentication";
import { useContext } from "react";
import { ActiveCompanyContext, ActiveCustomerContext } from "AppContextWrapper";
import { setUser as setSentryUser } from "@sentry/react";
import {
    LogoutDocument,
    useLogoutMutation,
} from "Apollo/Mutations/AuthMutations/index.generated";
import { useHandleError } from "Entity/ErrorHandling/ErrorHandler";
import { AnyError } from "Utils/errors";

export function useHandleLogout() {
    const navigate = useNavigate();
    const hookedClient = useApolloClient();
    const handleError = useHandleError();
    const [logout] = useLogoutMutation({
        fetchPolicy: "network-only",
    });
    const { setActiveCompany } = useContext(ActiveCompanyContext);
    const { setActiveCustomer } = useContext(ActiveCustomerContext);
    const { setFunctionality } = useContext(NorreFunctionalityContext);
    const { setActiveAuthUser } = useContext(ActiveAuthUserContext);
    const { setActiveNorreUser } = useContext(ActiveNorreUserContext);

    return async () => {
        const hadTokens = hasValidToken();
        navigate(LOGIN_ABSOLUTE);
        setActiveCompany(null);
        setActiveCustomer(null);
        setFunctionality(null);
        setActiveAuthUser(null);
        setActiveNorreUser(null);
        setSentryUser(null);
        clearTokenExpiration();
        try {
            if (hadTokens) {
                await logout();
            }
            await hookedClient.clearStore();
        } catch (e) {
            handleError(e as AnyError);
        }
    };
}

/**
 * When another tab cleared auth storage, navigate to login and reset client state
 * without calling the GraphQL logout mutation (session already ended elsewhere).
 */
export function useHandleLogoutFromOtherTab() {
    const navigate = useNavigate();
    const hookedClient = useApolloClient();
    const handleError = useHandleError();
    const { setActiveCompany } = useContext(ActiveCompanyContext);
    const { setActiveCustomer } = useContext(ActiveCustomerContext);
    const { setFunctionality } = useContext(NorreFunctionalityContext);
    const { setActiveAuthUser } = useContext(ActiveAuthUserContext);
    const { setActiveNorreUser } = useContext(ActiveNorreUserContext);

    return async () => {
        navigate(LOGIN_ABSOLUTE);
        setActiveCompany(null);
        setActiveCustomer(null);
        setFunctionality(null);
        setActiveAuthUser(null);
        setActiveNorreUser(null);
        setSentryUser(null);
        clearTokenExpiration();
        try {
            await hookedClient.clearStore();
        } catch (e) {
            handleError(e as AnyError);
        }
    };
}

export const handleLogoutWithoutHook = async () => {
    const hadTokens = hasValidToken();
    deleteActiveOrgCompany();
    deleteActiveOrgCustomer();
    deleteActiveUserStorage();
    deleteActiveNorreUserStorage();
    removeNorreFunctionalityStorage();
    setSentryUser(null);
    clearTokenExpiration();
    // Server-side logout is best effort: when the backend is unreachable the
    // local logout and the reload must still complete, otherwise the user is
    // left half logged out and the rejection escapes to Sentry.
    try {
        if (hadTokens) {
            await client.mutate({
                mutation: LogoutDocument,
                fetchPolicy: "network-only",
            });
        }
    } catch (e) {
        console.error("Logout mutation failed, continuing local logout", e);
    }
    try {
        await client.clearStore();
    } catch (e) {
        console.error("clearStore failed during logout", e);
    }
    // eslint-disable-next-line no-restricted-globals
    location.reload();
};
